import { makeStyles } from '@material-ui/core/styles'
import {
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_FOCUS_BORDER,
} from 'lib/form-field-styles'
import {
  SEGMENTED_BUTTON_BORDER,
  SEGMENTED_BUTTON_CHECK_SIZE,
} from 'lib/segmented-button-styles'

export const SELECTED_CHIP_BACKGROUND = '#E0EAFF'
export const SELECTED_CHIP_FOREGROUND = '#001C57'
export const SELECTED_CHIP_HOVER_BACKGROUND = '#D4E2FF'

export const composeOptionBorder = `1px solid ${SEGMENTED_BUTTON_BORDER}`

export const composeSelectedCalloutSurface = {
  backgroundColor: SELECTED_CHIP_BACKGROUND,
  borderColor: OUTLINED_INPUT_FOCUS_BORDER,
  '&:hover': {
    backgroundColor: SELECTED_CHIP_HOVER_BACKGROUND,
    borderColor: OUTLINED_INPUT_FOCUS_BORDER,
  },
}

const COMPOSE_OPTION_HOVER_BORDER = '#A3A9AC'
const COMPOSE_OPTION_HOVER_BACKGROUND = '#F7F9FA'

export const composeOptionInteractionStates = {
  '&:hover': {
    borderColor: COMPOSE_OPTION_HOVER_BORDER,
    backgroundColor: COMPOSE_OPTION_HOVER_BACKGROUND,
  },
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible, &.Mui-focusVisible': {
    outline: `2px solid ${OUTLINED_INPUT_FOCUS_BORDER}`,
    outlineOffset: 2,
  },
}

export const getComposeSelectionCheckStyles = theme => ({
  position: 'absolute',
  top: theme.spacing(1.5),
  right: theme.spacing(1.5),
  fontSize: SEGMENTED_BUTTON_CHECK_SIZE,
  color: OUTLINED_INPUT_FOCUS_BORDER,
  pointerEvents: 'none',
})

export const useCoachBookStudentComposeStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 12,
    width: '100%',
    maxWidth: 640,
    maxHeight: 'calc(100vh - 32px)',
    margin: theme.spacing(2),
    // Theme sets paper overflow hidden; keep shell clipped and scroll content.
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  dialogShell: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  dialogContent: {
    padding: theme.spacing(0.5, 2.5, 1.5),
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  dialogActions: {
    flex: '0 0 auto',
    padding: theme.spacing(1.5, 2.5, 2.5),
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
  },
  // Horizontal padding matches the dialog content's 24px (theme MuiDialogContent
  // wins over dialogContent above) so the row lines up with the fields.
  paymentLinkOption: {
    flex: '0 0 auto',
    padding: theme.spacing(1.5, 3, 0),
  },
  // Doubled selectors in the payment link styles win over MUI's
  // FormControlLabel, Checkbox, and Typography sheets, which are injected
  // after this one.
  paymentLinkLabel: {
    '&&': {
      display: 'flex',
      alignItems: 'flex-start',
      width: '100%',
      boxSizing: 'border-box',
      margin: 0,
      padding: theme.spacing(1.5, 2),
      border: composeOptionBorder,
      borderRadius: 12,
      backgroundColor: theme.palette.common.white,
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        borderColor: COMPOSE_OPTION_HOVER_BORDER,
        backgroundColor: COMPOSE_OPTION_HOVER_BACKGROUND,
      },
    },
  },
  // Negative margins cancel the small checkbox's 9px ripple padding so the
  // 20px box sits on the label's first 20px line.
  paymentLinkCheckbox: {
    '&&': {
      margin: '-9px 3px -9px -9px',
      '&.Mui-checked': {
        color: OUTLINED_INPUT_FOCUS_BORDER,
      },
      '&.Mui-checked:hover': {
        backgroundColor: 'rgba(0, 83, 255, 0.04)',
      },
    },
  },
  paymentLinkTitle: {
    '&&': {
      fontSize: '0.875rem',
      lineHeight: '20px',
      fontWeight: 500,
      color: CALLOUT_FOREGROUND,
      overflowWrap: 'anywhere',
    },
  },
  paymentLinkHelper: {
    '&&': {
      marginTop: 2,
      fontSize: '0.8125rem',
      lineHeight: '18px',
      color: theme.palette.text.secondary,
    },
  },
  composeSections: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  composeSection: {
    gap: theme.spacing(1),
  },
  viewMoreButton: {
    alignSelf: 'flex-start',
  },
  composeSectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  searchField: {
    width: '100%',
  },
  searchPopper: {
    zIndex: 1500,
  },
  studentOptionRow: {
    minHeight: 56,
    boxSizing: 'border-box',
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&[data-focus="true"]': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&[aria-selected="true"]': {
      backgroundColor: SELECTED_CHIP_BACKGROUND,
    },
    '&[aria-selected="true"][data-focus="true"]': {
      backgroundColor: SELECTED_CHIP_HOVER_BACKGROUND,
    },
  },
  studentOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    width: '100%',
    minWidth: 0,
  },
  studentOptionText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  // Doubled selector so it wins over MuiTypography-body1, which is injected
  // after this sheet.
  studentOptionEmail: {
    '&&': {
      fontSize: '12px',
      lineHeight: '16px',
      color: '#6B7C87',
    },
  },
  // Doubled selector so it wins over the shared list avatar size.
  selectedStudentAvatar: {
    '&&': {
      width: 24,
      height: 24,
      fontSize: '10px',
      marginLeft: theme.spacing(0.75),
    },
  },
  composeSelectionCheck: getComposeSelectionCheckStyles(theme),
  lessonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  lessonCard: {
    position: 'relative',
    borderRadius: 12,
    border: composeOptionBorder,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1.5, 4, 1.5, 2),
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    ...composeOptionInteractionStates,
  },
  lessonCardSelected: {
    ...composeSelectedCalloutSurface,
    '& $lessonCardTitle': {
      color: SELECTED_CHIP_FOREGROUND,
    },
  },
  lessonCardTitleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing(1),
    minWidth: 0,
  },
  lessonCardTitle: {
    fontWeight: 600,
    fontSize: '0.9375rem',
    color: CALLOUT_FOREGROUND,
    lineHeight: 1.35,
  },
  lessonCardMeta: {
    '&:first-of-type': {
      marginTop: theme.spacing(0.5),
    },
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
  availabilityEmptyMessage: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  whenWhereFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'start',
    // Outlined floating labels overhang the field top by ~8px; with the
    // parent's 12px gap this reads as ~24px below the tip.
    '&:not(:first-child)': {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  customTimeWarnings: {
    gridColumn: '1 / -1',
  },
  // Keeps the Book and Booking… states the same width; internal padding stays
  // the contained-button default (8px 16px) like other buttons with an icon.
  bookButton: {
    minWidth: 96,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2.5),
    '& .MuiButton-startIcon': {
      marginLeft: 0,
    },
  },
}))
